import { useCallback, useMemo } from "react"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import { Button, Form, FormFeedback, Label } from "reactstrap"
import * as Yup from "yup"

import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import {
  useCreateEmployeeDepartment,
  useEditEmployeeDepartment,
} from "hooks/useEmployeeDepartment"

const EmployeeDepartmentAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  editedData,
}) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateEmployeeDepartment()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditEmployeeDepartment()

  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Please enter department name")
      .max(255, "Name cannot exceed 255 characters"),
    description: Yup.string().max(
      1000,
      "Description cannot exceed 1000 characters"
    ),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const action = isEdit
          ? editMutation({ id: editedData.id, ...values })
          : createMutation(values)

        const res = await action
        toast.success(
          res?.message ??
            `Department ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error(
          error?.response?.data?.message ?? "Failed to save department."
        )
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editedData?.name || "",
      description: editedData?.description || "",
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit,
  })

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Department"}${loading ? "..." : ""}`
  }, [isEdit, editLoading, createLoading])

  const isSubmitDisabled = editLoading || createLoading

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Department" : "Add Department"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <div className="mb-3">
          <Label htmlFor="name">
            Name <span className="text-danger">*</span>
          </Label>
          <InputField
            type="text"
            name="name"
            id="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched.name && !!formik.errors.name}
          />
          {formik.touched.name && formik.errors.name && (
            <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
          )}
        </div>

        <div className="mb-3">
          <Label htmlFor="description">Description</Label>
          <InputField
            type="textarea"
            name="description"
            id="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={
              formik.touched.description && !!formik.errors.description
            }
          />
          {formik.touched.description && formik.errors.description && (
            <FormFeedback type="invalid">
              {formik.errors.description}
            </FormFeedback>
          )}
        </div>

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default EmployeeDepartmentAddEdit
